import { Controller } from "@hotwired/stimulus"
import { animate, spring } from "motion"
import { debug } from "../utils/debug"

/**
 * Product Filter Controller
 *
 * Handles client-side filtering and sorting of product grid items.
 * Supports multi-select filters, URL hash state, finish/color switching,
 * and animated image swaps using Motion library.
 *
 * @controller product-filter
 * @target {HTMLElement} item - Product card elements to filter/sort
 * @target {HTMLElement} filter - Filter dropdown groups
 * @target {HTMLSelectElement} sort - Sort order select element
 * @value {String} sortBy - Current sort field (price, rating, name, best-seller)
 * @value {String} sortDirection - Sort direction (asc, desc)
 *
 * @action filter - Apply filter from checkbox change
 * @action sort - Apply sort from select change
 * @action resetFilters - Clear all filters
 * @action onSwatchClick - Handle finish/color swatch clicks
 *
 * @example Product grid with filters
 * <div data-controller="product-filter">
 *   <!-- Filter dropdowns -->
 *   <div data-product-filter-target="filter" data-group="type">
 *     <input type="checkbox" value=".cable" id="type-cable">
 *     <label for="type-cable">Cable</label>
 *   </div>
 *
 *   <!-- Sort select -->
 *   <select data-product-filter-target="sort" data-action="change->product-filter#sort">
 *     <option value="bs">Best Sellers</option>
 *     <option value="asc">Price: Low to High</option>
 *   </select>
 *
 *   <!-- Product cards -->
 *   <div data-product-filter-target="item"
 *        data-type="cable"
 *        data-price="299"
 *        data-rating="4.5">
 *     <!-- Product card content -->
 *   </div>
 * </div>
 *
 * @notes
 * - Supports URL hash params: #filter=type&value=cable
 * - Enforces single-select within each filter group
 * - Animated image swap on finish/swatch click
 *
 * @used-in app/views/www/showcases/index.html.erb
 * @used-in app/views/pages/towel-warmer.html.erb
 * @used-in app/views/www/snow_melting/controls.html.erb
 */
export default class extends Controller {
  static targets = ["item", "filter", "sort"]
  static values = {
    sortBy: String,
    sortDirection: String
  }

  connect() {
    // Handle URL parameters to preset filters with a small delay to ensure DOM is ready
    setTimeout(() => {
      this.handleUrlParameters()
    }, 100)

    this.updateVisibility()
    this.updateFilterVisualStates()
    this.applySelectedFinishToVisible()

    // Listen for hash changes to handle navigation within the same page
    this.hashChangeHandler = this.handleHashChange.bind(this)
    window.addEventListener('hashchange', this.hashChangeHandler)
  }

  disconnect() {
    // Clean up the hash change listener
    if (this.hashChangeHandler) {
      window.removeEventListener('hashchange', this.hashChangeHandler)
    }
  }

  handleHashChange() {
    // Reset all filters first
    this.resetFilters()

    // Then apply the new filter from the hash
    setTimeout(() => {
      this.handleUrlParameters()
    }, 50)
  }

  handleUrlParameters() {
    // Read from hash fragment instead of query parameters
    const hash = window.location.hash.substring(1) // Remove the # symbol
    const urlParams = new URLSearchParams(hash)
    const filterParam = urlParams.get('filter')
    const valueParam = urlParams.get('value')

    debug('product-filter', 'Hash parameters:', { filterParam, valueParam })
    debug('product-filter', 'Available filter targets:', this.filterTargets.length)

    if (filterParam && valueParam) {
      debug('product-filter', `Setting filter: ${filterParam} = ${valueParam}`)

      // Find the filter group
      const filterGroup = Array.from(this.filterTargets).find(f =>
        f.dataset.group === filterParam
      )

      debug('product-filter', 'Found filter group:', filterGroup)

      if (filterGroup) {
        // Find the checkbox for this value
        const checkbox = filterGroup.querySelector(`input[value=".${valueParam}"]`)

        debug('product-filter', 'Found checkbox:', checkbox)

        if (checkbox) {
          // Check the checkbox
          checkbox.checked = true

          // Update the visual state of the filter dropdown
          this.updateFilterVisualStates()

          // Trigger the filter logic
          this.filter({ target: checkbox })

          // Scroll to the filter section
          this.scrollToFilters()

          debug('product-filter', `Successfully set ${filterParam} filter to ${valueParam}`)
        } else {
          debug.warn('product-filter', `Could not find checkbox for ${filterParam} = ${valueParam}`)
          // Debug: list all available checkboxes
          const allCheckboxes = filterGroup.querySelectorAll('input[type="checkbox"]')
          debug('product-filter', 'Available checkboxes:', Array.from(allCheckboxes).map(cb => ({ value: cb.value, id: cb.id })))
        }
      } else {
        debug.warn('product-filter', `Could not find filter group for ${filterParam}`)
        // Debug: list all available filter groups
        debug('product-filter', 'Available filter groups:', Array.from(this.filterTargets).map(f => f.dataset.group))
      }
    }
  }

  scrollToFilters() {
    const filterSection = document.getElementById('product-filters')
    if (filterSection) {
      // Scroll to the filter section with smooth behavior
      filterSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  filter(event) {
    // Handle single-select behavior for connection-type filter
    if (event && event.target) {
      const checkbox = event.target
      const filterGroup = checkbox.closest('[data-product-filter-target="filter"]')
      const groupName = filterGroup.dataset.group

      // If this is the connection-type filter, make it single-select
      // Enforce single-select for ALL filter groups
      if (checkbox.type === 'checkbox' && checkbox.checked) {
        const allInGroup = filterGroup.querySelectorAll('input[type="checkbox"]')
        allInGroup.forEach(other => {
          other.checked = (other === checkbox)
        })
      }

      // If Finish was changed, propagate finish choice to cards (image + URLs)
      if (groupName === 'finish') {
        if (checkbox.checked) {
          const selectedFinishClass = checkbox.id // already parameterized
          this.applyFinishToCards(selectedFinishClass)
        } else {
          // if unchecked and another remains checked, apply the remaining one; else do nothing
          const remaining = Array.from(filterGroup.querySelectorAll('input[type="checkbox"]:checked'))[0]
          if (remaining) {
            this.applyFinishToCards(remaining.id)
          }
        }
      }
    }

    this.updateVisibility()
    this.updateFilterVisualStates()
    this.applySelectedFinishToVisible()
  }

  applyFinishToCards(finishClass) {
    if (!finishClass) return
    const visibleItems = this.itemTargets
      .filter(item => item.style.display !== 'none')

    visibleItems.forEach(item => {
      // Find the indicator li for this finish
      const indicator = item.querySelector(`.carousel-indicators .filter-swatch.${finishClass}`)
      if (!indicator) return

      const newUrl = indicator.getAttribute('data-url-button')
      const modelId = indicator.getAttribute('data-model')
      const newImg = indicator.getAttribute('data-image-src')
      const newIndex = parseInt(indicator.getAttribute('data-index') || '0', 10)

      // Swap card top image
      if (newImg) {
        const img = item.querySelector('img.card-img-top')
        if (img) {
          const oldIndex = parseInt(img.getAttribute('data-current-index') || '0', 10)
          this.swapCardImage(img, {
            newImg,
            newIndex,
            direction: newIndex > oldIndex ? 'right' : newIndex < oldIndex ? 'left' : null
          })
        }
      }

      // Update all Explore/thumbnail links for this model to the selected variant URL
      if (modelId && newUrl) {
        document.querySelectorAll(`.${modelId}-url-button`).forEach(a => {
          if (a && a.href !== newUrl) a.href = newUrl
        })
      }
    })
  }

  onSwatchClick(event) {
    event.preventDefault()
    event.stopPropagation()
    if (event.stopImmediatePropagation) event.stopImmediatePropagation()
    const swatch = event.currentTarget
    if (!swatch) return

    // Detect the finish class robustly by ignoring utility/lazy classes
    const ignore = new Set(['filter-swatch', 'lazyload', 'lazyloaded', 'o-hidden', 'ms-0', 'me-2'])
    const finishClass = Array.from(swatch.classList).find(c => !ignore.has(c))
    const newUrl = swatch.getAttribute('data-url-button')
    const modelId = swatch.getAttribute('data-model')
    const newImg = swatch.getAttribute('data-image-src')
    const newIndex = parseInt(swatch.getAttribute('data-index') || '0', 10)

    // Swap the main image src of this card (card-img-top)
    if (newImg && modelId) {
      const card = swatch.closest('.card')
      const img = card ? card.querySelector('img.card-img-top') : null
      if (img) {
        const oldIndex = parseInt(img.getAttribute('data-current-index') || '0', 10)
        this.swapCardImage(img, {
          newImg,
          newIndex,
          direction: newIndex > oldIndex ? 'right' : newIndex < oldIndex ? 'left' : null
        })
      }
    }

    // Update indicator active state within this set
    const indicatorList = swatch.parentElement
    if (indicatorList) {
      indicatorList.querySelectorAll('li').forEach(li => li.classList.remove('active'))
      swatch.classList.add('active')
    }

    // Update links for this model
    if (modelId && newUrl) {
      document.querySelectorAll(`.${modelId}-url-button`).forEach(a => {
        if (a && a.href !== newUrl) a.href = newUrl
      })
    }
  }

  getSelectedFinishClass() {
    const finishGroup = Array.from(this.filterTargets).find(f => f.dataset.group === 'finish')
    if (!finishGroup) return null
    const checked = finishGroup.querySelector('input[type="checkbox"]:checked')
    return checked ? checked.id : null
  }

  applySelectedFinishToVisible() {
    const selectedFinish = this.getSelectedFinishClass()
    if (selectedFinish) {
      this.applyFinishToCards(selectedFinish)
    }
  }

  swapCardImage(img, { newImg, newIndex, direction }) {
    if (!newImg) return

    const setImage = () => {
      if (img.dataset) {
        img.dataset.src = newImg
      }
      img.setAttribute('src', newImg)
      img.setAttribute('data-current-index', String(newIndex))
    }

    if (!direction) {
      setImage()
      return
    }

    const offset = direction === 'right' ? 40 : -40
    const runAnimation = () => {
      img.style.transform = `translateX(${offset}px)`
      img.style.opacity = '0'

      requestAnimationFrame(() => {
        setImage()
        animate(
          img,
          { x: [offset, 0], opacity: [0, 1] },
          { duration: 0.45, easing: spring({ stiffness: 160, damping: 22, keyframes: [0, 1] }) }
        )
      })
    }

    if (img.dataset.src === newImg) {
      runAnimation()
      return
    }

    const preload = new Image()
    preload.onload = runAnimation
    preload.onerror = runAnimation
    preload.src = newImg
  }

  sort(event) {
    const sortValue = event.target.value
    let sortBy, sortDirection

    switch(sortValue) {
      case 'bs':
        sortBy = 'best-seller'
        sortDirection = 'desc'
        break
      case 'asc':
        sortBy = 'price'
        sortDirection = 'asc'
        break
      case 'dec':
        sortBy = 'price'
        sortDirection = 'desc'
        break
      case 'cr':
        sortBy = 'rating'
        sortDirection = 'desc'
        break
      case 'name':
        sortBy = 'name'
        sortDirection = 'asc'
        break
      default:
        sortBy = 'best-seller'
        sortDirection = 'desc'
    }

    this.sortByValue = sortBy
    this.sortDirectionValue = sortDirection
    this.sortItems()
  }

  resetFilters() {
    // Uncheck all filter checkboxes
    const allCheckboxes = document.querySelectorAll('[data-product-filter-target="filter"] input[type="checkbox"]')
    allCheckboxes.forEach(checkbox => {
      checkbox.checked = false
    })

    // Reset sort to default
    const sortSelect = document.getElementById('product-grid-sort-select')
    if (sortSelect) {
      sortSelect.value = 'bs'
    }

    // Update visibility and visual states
    this.updateVisibility()
    this.updateFilterVisualStates()
  }

  updateFilterVisualStates() {
    // Update visual states for filter groups
    const filterGroups = document.querySelectorAll('[data-product-filter-target="filter"]')

    filterGroups.forEach(filterGroup => {
      const allCheckboxes = filterGroup.querySelectorAll('input[type="checkbox"]')
      const checkedBoxes = filterGroup.querySelectorAll('input[type="checkbox"]:checked')
      // Button label lives in the dropdown toggle outside the menu
      const dropdown = filterGroup.closest('.dropdown')
      const buttonLabel = dropdown ? dropdown.querySelector('button .filter-label') : null

      // First, clear all individual checkbox visual states
      allCheckboxes.forEach(checkbox => {
        const label = filterGroup.querySelector(`label[for="${checkbox.id}"]`)
        if (label) {
          label.classList.remove('active', 'text-primary', 'fw-bold')
          label.style.backgroundColor = ''
          label.style.borderRadius = ''
          label.style.padding = ''
        }
      })

      // Then, add active states to checked checkboxes
      checkedBoxes.forEach(checkbox => {
        const label = filterGroup.querySelector(`label[for="${checkbox.id}"]`)
        if (label) {
          label.classList.add('active', 'text-primary', 'fw-bold')
          label.style.backgroundColor = '#e7f3ff'
          label.style.borderRadius = '4px'
          label.style.padding = '4px 8px'
        }
      })

      // Update filter group state
      if (checkedBoxes.length > 0) {
        // Add active state to filter group
        filterGroup.classList.add('filter-active')
        if (buttonLabel) {
          buttonLabel.classList.add('text-primary', 'fw-bold')

          // Update button text to show selected option
          const selectedOption = checkedBoxes[0]
          const optionLabel = filterGroup.querySelector(`label[for="${selectedOption.id}"] .filter-title`)
          if (optionLabel) {
            buttonLabel.textContent = optionLabel.textContent
          }
        }
        // Do not toggle the parent button's active state to avoid full red fill
      } else {
        // Remove active state from filter group
        filterGroup.classList.remove('filter-active')
        if (buttonLabel) {
          buttonLabel.classList.remove('text-primary', 'fw-bold')

          // Reset button text to default
          const groupName = filterGroup.dataset.group
          buttonLabel.textContent = groupName.charAt(0).toUpperCase() + groupName.slice(1).replace(/-/g, ' ')
        }
        // Leave the parent button styling unchanged
      }
    })
  }

  updateVisibility() {
    const activeFilters = this.getActiveFilters()

    let visibleCount = 0
    this.itemTargets.forEach(item => {
      const shouldShow = this.itemMatchesFilters(item, activeFilters)
      item.style.display = shouldShow ? 'block' : 'none'
      if (shouldShow) visibleCount += 1
    })

    this.updateEmptyState(visibleCount)
  }

  getActiveFilters() {
    const filters = {}

    this.filterTargets.forEach(filter => {
      const group = filter.dataset.group
      const checkedBoxes = filter.querySelectorAll('input[type="checkbox"]:checked')

      if (checkedBoxes.length > 0) {
        filters[group] = Array.from(checkedBoxes).map(cb => {
          // Remove the leading dot and convert to the format used in data attributes
          return cb.value.replace('.', '')
        })
      }
    })

    debug('product-filter', 'Active filters:', filters)
    return filters
  }

  updateEmptyState(visibleCount) {
    const grid = document.getElementById('product-grid')
    if (!grid) return

    let alertEl = document.getElementById('product-grid-empty-alert')
    if (!alertEl) {
      alertEl = document.createElement('div')
      alertEl.id = 'product-grid-empty-alert'
      alertEl.className = 'alert alert-warning d-none'
      alertEl.setAttribute('role', 'alert')
      alertEl.textContent = 'No products match your selected filters. Try adjusting filters or click Reset Filters.'
      grid.parentNode.insertBefore(alertEl, grid)
    }

    if (visibleCount === 0) {
      alertEl.classList.remove('d-none')
    } else {
      alertEl.classList.add('d-none')
    }
  }

  itemMatchesFilters(item, filters) {
    // If no filters are active, show all items
    if (Object.keys(filters).length === 0) {
      return true
    }

    // Check each filter group
    for (const [group, values] of Object.entries(filters)) {
      const itemValues = this.getItemAttributeValues(item, group)

      debug('product-filter', `Checking ${group}:`, {
        itemValues,
        filterValues: values,
        itemId: item.querySelector('[data-product-id]')?.getAttribute('data-product-id') || 'unknown'
      })

      // If item has no values for this attribute group, hide it
      if (itemValues.length === 0) {
        debug('product-filter', `No values for ${group}, hiding item`)
        return false
      }

      // Check if any of the item's values match ANY of the filter values
      // The item must have at least one of the selected filter values
      const hasAnyMatch = values.some(filterValue =>
        itemValues.some(itemValue => itemValue.includes(filterValue))
      )

      if (!hasAnyMatch) {
        debug('product-filter', `No match for ${group}, hiding item`)
        return false
      }
    }

    debug('product-filter', 'Item matches all filters, showing')
    return true
  }

  getItemAttributeValues(item, attributeGroup) {
    const values = []

    // Map filter group names to data attribute names
    const attributeMap = {
      'type': 'type',
      'connection-type': 'connection-type',
      'mounting': 'mounting-type',
      'finish': 'finishes',
      'style': 'style',
      'width': 'width',
      'height': 'height'
    }

    const dataAttributeName = attributeMap[attributeGroup] || attributeGroup
    const dataAttribute = `data-${dataAttributeName}`
    const attributeValue = item.getAttribute(dataAttribute)

    if (attributeValue) {
      values.push(...attributeValue.split(' '))
    }

    // Also check for CSS classes that might contain the attribute values
    const classList = Array.from(item.classList)
    classList.forEach(className => {
      if (className.includes(dataAttributeName.replace(/-/g, '-'))) {
        values.push(className)
      }
    })

    return values
  }

  sortItems() {
    const visibleItems = this.itemTargets.filter(item =>
      item.style.display !== 'none'
    )

    // Sort items based on current sort criteria
    visibleItems.sort((a, b) => {
      return this.compareItems(a, b)
    })

    // Re-append sorted items to maintain order
    const container = visibleItems[0]?.parentNode
    if (container) {
      visibleItems.forEach(item => {
        container.appendChild(item)
      })
    }
  }

  compareItems(a, b) {
    const sortBy = this.sortByValue
    const direction = this.sortDirectionValue

    let aValue, bValue

    switch(sortBy) {
      case 'price':
        aValue = parseFloat(a.dataset.price || '0')
        bValue = parseFloat(b.dataset.price || '0')
        break
      case 'rating':
        aValue = parseFloat(a.dataset.rating || '0')
        bValue = parseFloat(b.dataset.rating || '0')
        break
      case 'name':
        aValue = a.dataset.name || ''
        bValue = b.dataset.name || ''
        break
      case 'best-seller':
        aValue = a.dataset.bestSeller === 'true' ? 1 : 0
        bValue = b.dataset.bestSeller === 'true' ? 1 : 0
        break
      default:
        return 0
    }

    if (direction === 'asc') {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  }
}
